/**
 * labSummaryProjection — single source of truth for the doctor's Lab summary,
 * mirroring `procedureSummaryProjection`. One place derives the status buckets,
 * pill set, per-bucket counts, and row shape so the modal summary and the inline
 * Tests chips never drift (CLAUDE.md single-source rule).
 *
 * Status labels/colours come from `LAB_STATUS` in constants/lab.js; this only
 * owns the bucketing + projection.
 */
import { LAB_STATUS, labStatusLabel, isStandingLabOrder } from '../../constants/lab';

// The filter buckets, in display order (parity with PROCEDURE_PILLS). Per the
// product call, awaiting-payment / ready stay under "Planned".
export const LAB_PILLS = ['planned', 'in_progress', 'completed', 'cancelled'];

// planned is the default bucket (advised / standing / awaiting_payment / payment_cleared / scheduled).
const IN_PROGRESS = new Set(['sample_collected', 'in_progress']);
const COMPLETED = new Set(['completed', 'paid']);
const CANCELLED = new Set(['cancelled']);

export function labStatusGroup(status) {
  const s = String(status || '').toLowerCase();
  if (COMPLETED.has(s)) return 'completed';
  if (IN_PROGRESS.has(s)) return 'in_progress';
  if (CANCELLED.has(s)) return 'cancelled';
  return 'planned';
}

// Per-bucket pill metadata (label + dot colour). Dot colours are picked to read
// in both modes; the per-row dot uses the precise per-status colour from LAB_STATUS.
export const LAB_PILL_META = {
  planned: { label: 'Planned', color: '#f59e0b' },
  in_progress: { label: 'In progress', color: '#3b82f6' },
  completed: { label: 'Completed', color: '#10b981' },
  cancelled: { label: 'Cancelled', color: '#6b7280' },
};

// The dot colour for a specific status (used per-row). Falls back to the bucket colour.
export function labStatusColor(status) {
  return LAB_STATUS[String(status || '').toLowerCase()]?.fg
    || LAB_PILL_META[labStatusGroup(status)]?.color
    || '#94a3b8';
}

// Best lifecycle date for newest-first ordering.
function lifecycleDate(o) {
  return o?.completed_at || o?.started_at || o?.specimen_collected_at
    || o?.lab_queued_at || o?.updated_at || o?.created_at || '';
}

function toRow(o) {
  const cost = o?.final_cost != null ? Number(o.final_cost) : null;
  const isExternal = !!o?.is_external;
  const completedOutside = !!o?.completed_outside;
  // A done-outside completion reads "Done (outside)" instead of plain "Completed"
  // — the flag drives the label; the underlying bucket/status stay `completed`.
  const baseLabel = labStatusLabel(o?.status);
  const statusLabel = completedOutside ? 'Done (outside)' : baseLabel;
  return {
    id: o?.lab_order_id || null,
    name: o?.test_name || 'Lab test',
    status: String(o?.status || '').toLowerCase(),
    bucket: labStatusGroup(o?.status),
    catalogProcedureId: o?.catalog_procedure_id || null,
    isExternal,        // an external (done-outside) advice — no in-house run / billing
    completedOutside,  // completed via "mark done outside" (vs an in-house run)
    // Show the technician's NAME only — never the raw member_id UUID.
    assignee: o?.assigned_to?.name || null,
    assigned: !!o?.assigned_to?.member_id,
    originDoctor: o?.origin_doctor_name || o?.visit_doctor_name || null,
    cancelledBy: o?.cancelled_by_name || null,
    completedByName: o?.completed_by_name || null,
    completionNote: o?.completion_note || null,
    // Lifecycle dates for the row (advised / completed / cancelled).
    advisedAt: o?.origin_suggested_at || o?.created_at || null,
    completedAt: o?.completed_at || null,
    cancelledAt: o?.cancelled_at || null,
    expiresAt: o?.expires_at || null,
    standing: isStandingLabOrder(o),
    // External tests aren't billed in-house — never show a ₹ amount for them.
    finalCost: !isExternal && cost != null && !Number.isNaN(cost) && cost > 0 ? cost : null,
    carriedAway: !!o?.carried_away,
    fromHistory: !!o?.from_history, // a recent completed lab from another visit (review)
    statusLabel,
    order: o, // raw, for LabReportActions / actions
  };
}

// Repeat-test awareness: map catalog_procedure_id → most-recent COMPLETED date
// for the patient, so the advise dropdown can warn "last done N days ago".
export function lastDoneByProcedure(orders = []) {
  const map = {};
  for (const o of orders) {
    if (!COMPLETED.has(String(o?.status || '').toLowerCase())) continue;
    const pid = o?.catalog_procedure_id;
    const when = o?.completed_at || o?.updated_at || o?.created_at;
    if (!pid || !when) continue;
    if (!map[pid] || String(when) > String(map[pid])) map[pid] = when;
  }
  return map;
}

// "last done N days ago" label from an ISO date (or null if not recently done).
export function lastDoneLabel(iso) {
  if (!iso) return null;
  const then = new Date(String(iso).slice(0, 10));
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'last done today';
  if (days === 1) return 'last done yesterday';
  return `last done ${days}d ago`;
}

/**
 * buildLabSummary(orders) → { rows (newest-first), counts }.
 * Dedupes by lab_order_id; counts per bucket incl. `all`.
 */
export function buildLabSummary(orders = []) {
  const seen = new Set();
  const rows = [];
  for (const o of orders) {
    const id = o?.lab_order_id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    rows.push(toRow(o));
  }
  rows.sort((a, b) => String(lifecycleDate(b.order)).localeCompare(String(lifecycleDate(a.order))));

  const counts = { all: rows.length, planned: 0, in_progress: 0, completed: 0, cancelled: 0 };
  for (const r of rows) counts[r.bucket] = (counts[r.bucket] || 0) + 1;
  return { rows, counts };
}
